import { documentsEdgeFetch } from '../../lib/documents-edge';
import { getSupabaseUrl, getSupabaseFunctionRegion } from '../../config/global';

import type {
  FileLink,
  LibraryView,
  LibrarySort,
  FolderRecord,
  DocumentsUsage,
  LibraryResponse,
  OutcomesResponse,
  OpenUploadResponse,
  CompleteUploadResponse,
  UnfinishedUpload,
  DocumentSource,
  DocumentSourceRef,
  DocumentSearchResponse,
} from './documents-api-types';

export const DOCUMENTS_PAGE_SIZE = 100;

export type LibraryQuery = {
  view: LibraryView;
  folderId: string | null;
  q: string;
  kind: string | null;
  sort: LibrarySort;
  dir: 'asc' | 'desc';
};

export function libraryPath(query: LibraryQuery, offset: number, limit = DOCUMENTS_PAGE_SIZE): string {
  const params = new URLSearchParams({ view: query.view, sort: query.sort, dir: query.dir });
  if (query.folderId) params.set('folderId', query.folderId);
  if (query.q) params.set('q', query.q);
  if (query.kind) params.set('kind', query.kind);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return `library?${params.toString()}`;
}

export function fetchLibrary(query: LibraryQuery, offset = 0): Promise<LibraryResponse> {
  return documentsEdgeFetch<LibraryResponse>(libraryPath(query, offset));
}

/** The same search Ari uses: words inside documents, and meaning. */
export function searchDocuments(query: string, limit: number): Promise<DocumentSearchResponse> {
  return documentsEdgeFetch<DocumentSearchResponse>('search', { method: 'POST', body: { query, limit } });
}

/** The place each source is, as the cloud stored it when it read the document. */
export async function fetchSources(sources: DocumentSourceRef[]): Promise<DocumentSource[]> {
  const body = await documentsEdgeFetch<{ sources: DocumentSource[] }>('sources', {
    method: 'POST',
    body: { sources },
  });
  return body.sources;
}

/** Library entries for documents a search found, wherever they are. */
export function fetchDocumentsByIds(ids: string[]): Promise<LibraryResponse> {
  const params = new URLSearchParams({ view: 'recent', ids: ids.join(','), limit: String(ids.length) });
  return documentsEdgeFetch<LibraryResponse>(`library?${params.toString()}`);
}

export async function fetchFolders(): Promise<FolderRecord[]> {
  const body = await documentsEdgeFetch<{ folders: FolderRecord[] }>('folders');
  return body.folders;
}

export function fetchUsage(): Promise<DocumentsUsage> {
  return documentsEdgeFetch<DocumentsUsage>('usage');
}

export function createFolder(name: string, parentId: string | null) {
  return documentsEdgeFetch<{ folder: FolderRecord; renamed: boolean }>('folders', {
    method: 'POST',
    body: { name, parentId },
  });
}

export function updateFolder(id: string, change: { name: string } | { parentId: string | null }) {
  return documentsEdgeFetch<{ folder: FolderRecord; renamed: boolean }>(`folders/${id}`, {
    method: 'PATCH',
    body: change,
  });
}

export function updateFile(id: string, change: { name: string } | { folderId: string | null }) {
  return documentsEdgeFetch<{ document: { id: string; name: string; folderId: string | null } }>(`files/${id}`, {
    method: 'PATCH',
    body: change,
  });
}

export function fetchFileLink(id: string, disposition: FileLink['disposition']): Promise<FileLink> {
  return documentsEdgeFetch<FileLink>(`files/${id}/url?disposition=${disposition}`);
}

/** The original of a sheet, read with fetch and drawn as a table. The access log says view. CLOUD-1159. */
export function fetchTableLink(id: string): Promise<FileLink> {
  return documentsEdgeFetch<FileLink>(`files/${id}/url?kind=table`);
}

export function fetchPreviewLink(id: string): Promise<FileLink> {
  return documentsEdgeFetch<FileLink>(`files/${id}/url?kind=preview`);
}

export function retryFile(id: string) {
  return documentsEdgeFetch<{ status: 'queued' }>(`files/${id}/retry`, { method: 'POST' });
}

export type ItemIds = { fileIds: string[]; folderIds: string[] };

/** The most ids of each kind one delete or restore takes. */
const MAX_IDS_PER_REQUEST = 500;

async function inBatches(ids: ItemIds, send: (batch: ItemIds) => Promise<OutcomesResponse>) {
  const results: OutcomesResponse['results'] = [];
  const count = Math.max(ids.fileIds.length, ids.folderIds.length, 1);
  for (let i = 0; i < count; i += MAX_IDS_PER_REQUEST) {
    const batch = {
      fileIds: ids.fileIds.slice(i, i + MAX_IDS_PER_REQUEST),
      folderIds: ids.folderIds.slice(i, i + MAX_IDS_PER_REQUEST),
    };
    results.push(...(await send(batch)).results);
  }
  return { results };
}

export function deleteItems(ids: ItemIds, permanent = false): Promise<OutcomesResponse> {
  return inBatches(ids, (batch) =>
    documentsEdgeFetch<OutcomesResponse>('delete', { method: 'POST', body: { ...batch, permanent } })
  );
}

export function restoreItems(ids: ItemIds): Promise<OutcomesResponse> {
  return inBatches(ids, (batch) => documentsEdgeFetch<OutcomesResponse>('restore', { method: 'POST', body: batch }));
}

export function emptyBin(): Promise<{ purging: number }> {
  return documentsEdgeFetch<{ purging: number }>('bin/empty', { method: 'POST' });
}

export type OpenUploadBody = {
  clientUploadId: string;
  folderId: string | null;
  name: string;
  mimeType: string;
  byteSize: number;
  checksumSha256: string;
  sampledSha256: string;
  replaceDocumentId?: string;
};

export function openUpload(body: OpenUploadBody): Promise<OpenUploadResponse> {
  return documentsEdgeFetch<OpenUploadResponse>('uploads', { method: 'POST', body });
}

export function completeUpload(sessionId: string) {
  return documentsEdgeFetch<CompleteUploadResponse>(`uploads/${sessionId}/complete`, { method: 'POST' });
}

export function abortUpload(sessionId: string) {
  return documentsEdgeFetch<{ status: string }>(`uploads/${sessionId}/abort`, { method: 'POST' });
}

export type HeartbeatBody = { resumeLocation?: string; sentBytes?: number };

/** A sign of life while an upload runs. Without it the upload gives its room back after a few minutes. */
export function heartbeatUpload(sessionId: string, body: HeartbeatBody) {
  // completed: the cloud finished it already. The upload goes on, and its completion answers stored.
  return documentsEdgeFetch<{ status: 'live' | 'completed' }>(`uploads/${sessionId}/heartbeat`, {
    method: 'POST',
    body,
  });
}

/** Gives the room of these uploads back at once. They stay open, so they can be resumed. */
export function releaseUploads(sessionIds: string[]) {
  return documentsEdgeFetch<{ released: number }>('uploads/release', { method: 'POST', body: { sessionIds } });
}

export async function fetchUnfinishedUploads(): Promise<UnfinishedUpload[]> {
  const body = await documentsEdgeFetch<{ uploads: UnfinishedUpload[] }>('uploads/unfinished');
  return body.uploads;
}

/** The same as releaseUploads, sent while the page closes. Nothing async may run then. */
export function releaseUploadsOnLeave(sessionIds: string[], token: string | undefined): void {
  if (!token || sessionIds.length === 0) return;
  const region = getSupabaseFunctionRegion();
  const query = region ? `?forceFunctionRegion=${encodeURIComponent(region)}` : '';
  fetch(`${getSupabaseUrl()}/functions/v1/root/documents/uploads/release${query}`, {
    method: 'POST',
    keepalive: true,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionIds: sessionIds.slice(0, 50) }),
  }).catch(() => undefined);
}
